import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchBox from "./Components/SearchBox";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY, BORDER_GREY, ENTERPRISE_TEAL } from "./colors";
import { Helmet } from "react-helmet";
import { useAuth } from "../auth/AuthContext";
import {
  listPortfolios,
  listPortfolioItems,
  createCheckoutSession,
  getPrices,
  getBillingStatus,
  ProductKey,
  Prices,
  PriceInfo,
  BillingStatus,
} from "../data/request";

type Tier = "free" | "pro" | "enterprise";

const PRICING_ROWS: { feature: string; description: string; minTier: Tier }[] = [
  { feature: "Company Search", description: "Search over 5 million UK companies filed with Companies House.", minTier: "free" },
  { feature: "Officers & Appointments", description: "Current and historic directors/secretaries, traced across every company they're appointed to.", minTier: "free" },
  { feature: "Charges & Filings", description: "Mortgages, charges, and the full filing history behind a company.", minTier: "free" },
  { feature: "Persons with Significant Control", description: "Who ultimately owns or controls a company, and the nature of that control.", minTier: "free" },
  { feature: "Ownership Structure", description: "A company's corporate owners and subsidiaries, mapped from the PSC register.", minTier: "pro" },
  { feature: "Property Ownership", description: "Land and property titles held by a company, from HM Land Registry.", minTier: "pro" },
  { feature: "Financials", description: "A real balance sheet and profit & loss with year-on-year trend charts.", minTier: "pro" },
  { feature: "Shareholders", description: "Exactly who owns a company's shares - name, share count and percentage.", minTier: "pro" },
  { feature: "ICO Data Protection Registration", description: "Whether a company is registered with the UK's data protection regulator (the ICO), its fee tier, and registration dates.", minTier: "pro" },
  { feature: "Advanced Search", description: "Filter companies by SIC code, status, location, or incorporation date.", minTier: "pro" },
  { feature: "Favourites", description: "Track companies you care about with one click, and revisit them from any page.", minTier: "pro" },
  { feature: "Gazette Insolvency Notices", description: "Winding-up petitions and other insolvency notices, often weeks before they reach Companies House.", minTier: "pro" },
  { feature: "Portfolios", description: "Track as many portfolios as you need - separate ones for different clients, industries, or watchlists.", minTier: "enterprise" },
  { feature: "Portfolio Email Alerts", description: "Get emailed when a company in one of your portfolios changes status, files something new, or gets a Gazette notice.", minTier: "enterprise" },
  { feature: "Dynamic Portfolios", description: "Save an Advanced Search filter and get emailed when a new company matches it - no manual re-checking.", minTier: "enterprise" },
  { feature: "PDF Company Reports", description: "A branded PDF summarising officers, financials, shareholders and ownership.", minTier: "enterprise" },
];

const TIER_RANK: Record<Tier, number> = { free: 0, pro: 1, enterprise: 2 };

const formatPrice = (info?: PriceInfo) => {
  if (!info) {
    return undefined;
  }
  const amount = info.amount / 100;
  const formatted = amount.toLocaleString("en-GB", {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  const symbol = info.currency.toUpperCase() === "GBP" ? "£" : `${info.currency.toUpperCase()} `;
  const suffix = info.interval === "month" ? "/mo" : info.interval === "year" ? "/yr" : "";
  return `${symbol}${formatted}${suffix}`;
};

// Shared by every purchase button below - a signed-out visitor sees the same
// pricing and buttons as everyone else (hiding them behind "sign in first"
// implied Pro was free with an account, which stopped being true), the
// button just intercepts the click instead of hitting Checkout directly.
const AuthGateDialog = (props: { open: boolean; onClose: () => void; action: string }) => {
  const navigate = useNavigate();
  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Sign in required</DialogTitle>
      <DialogContent>
        <DialogContentText>Please sign in or create a free account to {props.action}.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button onClick={() => navigate("/login")}>Sign In</Button>
        <Button variant="contained" onClick={() => navigate("/login?mode=signUp")} style={{ backgroundColor: DELTA_ACCENT }}>
          Sign Up
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// One button per billing cycle, rather than a dropdown - the price for each
// is already shown in the caption above, so the buttons just name the cycle.
const SubscribeButtons = (props: {
  monthlyKey: ProductKey;
  yearlyKey: ProductKey;
  color: string;
  signedIn: boolean;
}) => {
  const [gateOpen, setGateOpen] = useState(false);
  const [submitting, setSubmitting] = useState<ProductKey>();
  const [error, setError] = useState<string>();

  const handleSubscribe = async (productKey: ProductKey) => {
    if (!props.signedIn) {
      setGateOpen(true);
      return;
    }
    setError(undefined);
    setSubmitting(productKey);
    const doc = await createCheckoutSession(productKey);
    if (doc?.url) {
      window.location.href = doc.url;
      return;
    }
    setError(doc?.error ?? "Couldn't start checkout. Please try again.");
    setSubmitting(undefined);
  };

  return (
    <Box>
      <Box style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
        <Button
          size="small"
          variant="outlined"
          disabled={Boolean(submitting)}
          onClick={() => handleSubscribe(props.monthlyKey)}
          style={{ borderColor: props.color, color: props.color, textTransform: "none", width: 168 }}
        >
          {submitting === props.monthlyKey ? "Redirecting..." : "Subscribe for Monthly"}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={Boolean(submitting)}
          onClick={() => handleSubscribe(props.yearlyKey)}
          style={{ borderColor: props.color, color: props.color, textTransform: "none", width: 168 }}
        >
          {submitting === props.yearlyKey ? "Redirecting..." : "Subscribe for Yearly"}
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" style={{ color: "#C62828", display: "block", marginTop: 4, maxWidth: 160 }}>
          {error}
        </Typography>
      )}
      <AuthGateDialog open={gateOpen} onClose={() => setGateOpen(false)} action="subscribe" />
    </Box>
  );
};

// API Pro/Enterprise are single-price products (no monthly/yearly split), so
// a plain button suffices - no dropdown needed like the web SubscribeButton.
const ApiPurchaseButton = (props: { productKey: ProductKey; label: string; price?: PriceInfo; color: string; signedIn: boolean }) => {
  const [gateOpen, setGateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const handleClick = async () => {
    if (!props.signedIn) {
      setGateOpen(true);
      return;
    }
    setError(undefined);
    setSubmitting(true);
    const doc = await createCheckoutSession(props.productKey);
    if (doc?.url) {
      window.location.href = doc.url;
      return;
    }
    setError(doc?.error ?? "Couldn't start checkout. Please try again.");
    setSubmitting(false);
  };

  return (
    <Box>
      <Button
        size="small"
        variant="outlined"
        disabled={submitting}
        onClick={handleClick}
        style={{ borderColor: props.color, color: props.color, textTransform: "none" }}
      >
        {submitting ? "Redirecting..." : `${props.label}${props.price ? ` - ${formatPrice(props.price)}` : ""}`}
      </Button>
      {error && (
        <Typography variant="caption" style={{ color: "#C62828", display: "block", marginTop: 4, maxWidth: 200 }}>
          {error}
        </Typography>
      )}
      <AuthGateDialog open={gateOpen} onClose={() => setGateOpen(false)} action={`purchase ${props.label}`} />
    </Box>
  );
};

const TierCell = (props: { included: boolean }) =>
  props.included ? <CheckIcon style={{ color: DELTA_ACCENT }} fontSize="small" /> : <span style={{ color: BORDER_GREY }}>—</span>;

type PortfolioSummary = { portfolio_id: string; name: string };
type PortfolioPreviewItem = { company_number: string; company_name: string };

export const Home = () => {
  const auth = useAuth();
  const [portfolios, setPortfolios] = useState<PortfolioSummary[]>([]);
  const [items, setItems] = useState<Record<string, PortfolioPreviewItem[]>>({});
  const [prices, setPrices] = useState<Prices>();
  const [billingStatus, setBillingStatus] = useState<BillingStatus>();
  // Only auto-collapsed for Enterprise, whose sign-in already implies the
  // pitch is moot - Free/Pro visitors (signed in or not) still benefit from
  // seeing what upgrading buys them.
  const [pricingExpanded, setPricingExpanded] = useState(!auth.isEnterprise);

  useEffect(() => {
    setPricingExpanded(!auth.isEnterprise);
  }, [auth.isEnterprise]);

  useEffect(() => {
    getPrices().then(setPrices);
  }, []);

  useEffect(() => {
    if (auth.status !== "signedIn") {
      setBillingStatus(undefined);
      return;
    }
    getBillingStatus().then(setBillingStatus);
  }, [auth.status]);

  useEffect(() => {
    if (!auth.isPro) {
      return;
    }
    listPortfolios().then(async (doc) => {
      // At most 3 rows on the home page - a full breakdown belongs on the
      // dedicated Portfolio page, this is just a preview.
      const list: PortfolioSummary[] = (doc?.portfolios ?? []).slice(0, 3);
      setPortfolios(list);
      const itemsByPortfolio: Record<string, PortfolioPreviewItem[]> = {};
      await Promise.all(
        list.map(async (p) => {
          const itemsDoc = await listPortfolioItems(p.portfolio_id);
          itemsByPortfolio[p.portfolio_id] = itemsDoc?.items ?? [];
        })
      );
      setItems(itemsByPortfolio);
    });
  }, [auth.isPro]);

  const signedIn = auth.status === "signedIn";

  return (
    <Box>
      <Helmet>
        <title>Company Directory | DeltaGain</title>
      </Helmet>

      <Box style={{ marginBottom: 32 }}>
        <SearchBox />
      </Box>

      {auth.isPro && (
        <Paper elevation={0} style={{ padding: 24, marginBottom: 32 }}>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Typography variant="h6" style={{ color: DELTA_PRIMARY }}>
              {auth.isEnterprise ? "My Portfolios" : "Favourites"}
            </Typography>
            <Button component={RouterLink} to="/portfolio" size="small" style={{ color: DELTA_ACCENT }}>
              View All
            </Button>
          </Box>
          {portfolios.map((p) => {
            const portfolioItems = items[p.portfolio_id];
            return (
              <Box key={p.portfolio_id} style={{ marginBottom: 12 }}>
                {portfolios.length > 1 && (
                  <Typography variant="subtitle2" style={{ color: DELTA_PRIMARY, marginBottom: 6 }}>
                    {p.name}
                  </Typography>
                )}
                {portfolioItems === undefined ? null : portfolioItems.length === 0 ? (
                  <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
                    {p.portfolio_id === "default"
                      ? "You haven't added any companies to your Favourites yet - look for the star icon on a company page."
                      : "No companies added to this portfolio yet."}
                  </Typography>
                ) : (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {portfolioItems.map((item) => (
                      <Button
                        key={item.company_number}
                        component={RouterLink}
                        to={`/company/${item.company_number}`}
                        size="small"
                        variant="outlined"
                        style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT, textTransform: "none" }}
                      >
                        {item.company_name || item.company_number}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      <Accordion
        expanded={pricingExpanded}
        onChange={(_, expanded) => setPricingExpanded(expanded)}
        elevation={0}
        style={{ background: "transparent" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h4">Business intelligence on every UK company</Typography>
        </AccordionSummary>
        <AccordionDetails>
      <Typography
        variant="body1"
        style={{ color: TEXT_SECONDARY, maxWidth: 760, marginBottom: 32 }}
      >
        Company Directory turns Companies House filings into clear, actionable
        insight - built for due diligence, lead qualification, and credit
        checking on the businesses you work with.
      </Typography>

      <Paper elevation={0} style={{ overflow: "hidden" }}>
        <Box style={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: 220 }} />
              <TableCell align="center">
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: DELTA_PRIMARY }}>
                  Free
                </Typography>
                <Typography variant="caption" style={{ color: TEXT_SECONDARY }}>
                  No account needed
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: DELTA_ACCENT }}>
                  Pro†
                </Typography>
                <Typography variant="caption" style={{ color: TEXT_SECONDARY, display: "block", marginBottom: 6 }}>
                  {prices ? `${formatPrice(prices.pro_monthly)} or ${formatPrice(prices.pro_yearly)}` : " "}
                </Typography>
                {!auth.isPro && (
                  <SubscribeButtons
                    monthlyKey="pro_monthly"
                    yearlyKey="pro_yearly"
                    color={DELTA_ACCENT}
                    signedIn={signedIn}
                  />
                )}
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle1" style={{ fontWeight: 700, color: ENTERPRISE_TEAL }}>
                  Enterprise†
                </Typography>
                <Typography variant="caption" style={{ color: TEXT_SECONDARY, display: "block", marginBottom: 6 }}>
                  {prices ? `${formatPrice(prices.enterprise_monthly)} or ${formatPrice(prices.enterprise_yearly)}` : " "}
                </Typography>
                {!auth.isEnterprise && (
                  <SubscribeButtons
                    monthlyKey="enterprise_monthly"
                    yearlyKey="enterprise_yearly"
                    color={ENTERPRISE_TEAL}
                    signedIn={signedIn}
                  />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell colSpan={3} align="center" style={{ borderBottom: `1px solid ${BORDER_GREY}` }}>
                <Typography variant="caption">
                  <Link href="mailto:support@deltagain.co.uk?subject=Enterprise%20plan" style={{ color: ENTERPRISE_TEAL }}>
                    Need a custom contract? Contact Sales
                  </Link>
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PRICING_ROWS.map((row) => (
              <TableRow key={row.feature}>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 600, color: DELTA_PRIMARY }}>
                    {row.feature}
                  </Typography>
                  <Typography variant="caption" style={{ color: TEXT_SECONDARY }}>
                    {row.description}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <TierCell included={TIER_RANK.free >= TIER_RANK[row.minTier]} />
                </TableCell>
                <TableCell align="center">
                  <TierCell included={TIER_RANK.pro >= TIER_RANK[row.minTier]} />
                </TableCell>
                <TableCell align="center">
                  <TierCell included={TIER_RANK.enterprise >= TIER_RANK[row.minTier]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Paper>

      <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginTop: 16 }}>
        Need something bespoke - higher volume, a custom contract, or a plan
        that doesn't fit the table above?{" "}
        <Link href="mailto:support@deltagain.co.uk?subject=Company%20Directory%20-%20Custom%20Plan" style={{ color: DELTA_ACCENT }}>
          Contact us
        </Link>
        .
      </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Separately-priced add-on, not "included at this tier" like the
          checkmark table above - kept as its own card so it doesn't read as
          another row in PRICING_ROWS. */}
      <Paper
        elevation={0}
        style={{
          padding: 24,
          marginTop: 32,
          border: `1px solid ${BORDER_GREY}`,
        }}
      >
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <Box style={{ maxWidth: 560 }}>
            <Typography variant="h6" style={{ color: DELTA_PRIMARY, marginBottom: 4 }}>
              API Access†
            </Typography>
            <Typography variant="body2" style={{ color: TEXT_SECONDARY, marginBottom: 8 }}>
              Pull Company Directory data - search, officers, ownership,
              financials and compliance signals - straight into your own
              systems via a documented REST API.
            </Typography>
            <Button
              component={RouterLink}
              to="/api-docs"
              variant="outlined"
              size="small"
              style={{ borderColor: ENTERPRISE_TEAL, color: ENTERPRISE_TEAL, marginBottom: 8 }}
            >
              View API Docs
            </Button>
          </Box>
          <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!(
              billingStatus?.api_status === "active" &&
              (billingStatus?.api_plan === "api_pro" || billingStatus?.api_plan === "api_enterprise")
            ) && (
              <ApiPurchaseButton
                productKey="api_pro"
                label="API Pro"
                price={prices?.api_pro}
                color={DELTA_ACCENT}
                signedIn={signedIn}
              />
            )}
            {!(billingStatus?.api_plan === "api_enterprise" && billingStatus?.api_status === "active") && (
              <ApiPurchaseButton
                productKey="api_enterprise"
                label="API Enterprise"
                price={prices?.api_enterprise}
                color={ENTERPRISE_TEAL}
                signedIn={signedIn}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <Typography variant="caption" style={{ color: TEXT_SECONDARY, display: "block", marginTop: 24 }}>
        † All plans are subject to a fair usage policy of no more than 1,000 requests per day.
      </Typography>
    </Box>
  );
};
